import { useCallback, useMemo, useState } from "react";
import { useSupabaseCallback, useSupabaseLoader } from "../../../server/supabase/hooks";

import type { Database } from "../../../__definitions__/database-definitions";
import type { SupabaseHandlerArg } from "../../../server/supabase/hooks/types";

export type RecentlyEditedItem =
  Database["public"]["Functions"]["recently_edited"]["Returns"][number];

/** One kind of entity the feed can be narrowed to. */
export type RecentlyEditedType = RecentlyEditedItem["entity_type"];

export const PAGE_SIZE = 10;

interface RecentlyEdited {
  /** Null until the first page loads, or when it fails. */
  items: RecentlyEditedItem[] | null;
  /** While a reload or a change of filter loads, `items` are the ones shown before it. */
  reloading: boolean;
  error: Error | null;
  reload: () => void;
  hasMore: boolean;
  loadingMore: boolean;
  loadMoreError: Error | null;
  loadMore: () => Promise<void>;
  /** Puts something just made, such as a copy, at the top, until the feed next loads. */
  prepend: (item: RecentlyEditedItem) => void;
  /** Takes out something archived since the feed loaded. */
  remove: (id: string) => void;
}

const fetchPage = async (
  { supabase, account_id }: Pick<SupabaseHandlerArg<"public">, "supabase" | "account_id">,
  offset: number,
  userEdited: boolean,
  entityType: RecentlyEditedType | undefined,
): Promise<RecentlyEditedItem[]> => {
  const { data, error } = await supabase.rpc("recently_edited", {
    account_id_: account_id,
    size_: PAGE_SIZE,
    offset_: offset,
    user_edited_: userEdited,
    ...(entityType && { entity_type_: entityType }),
  });
  if (error) throw error;
  return data;
};

/**
 * The Communications, Surveys and Digital Assets most recently edited, newest first, a page at a
 * time. With `userEdited`, only those the user has edited themselves; with `entityType`, only that
 * kind.
 */
export function useRecentlyEdited(
  userEdited: boolean,
  entityType?: RecentlyEditedType,
): RecentlyEdited {
  const first = useSupabaseLoader(
    (ctx) => fetchPage(ctx, 0, userEdited, entityType),
    [userEdited, entityType],
  );

  // Pages after the first, kept against the first page they follow so a reload or a change of
  // filter drops them without an effect.
  const [more, setMore] = useState<{
    after: RecentlyEditedItem[];
    items: RecentlyEditedItem[];
    hasMore: boolean;
  } | null>(null);
  const [loadingMore, setLoadingMore] = useState(false);
  const [failedMore, setFailedMore] = useState<{
    after: RecentlyEditedItem[];
    error: Error;
  } | null>(null);

  // Made here since the first page loaded, newest first; kept against it like `more`.
  const [added, setAdded] = useState<{
    after: RecentlyEditedItem[];
    items: RecentlyEditedItem[];
  } | null>(null);

  // Archived here since the first page loaded; kept against it like `more`.
  const [removed, setRemoved] = useState<{
    after: RecentlyEditedItem[];
    ids: ReadonlySet<string>;
  } | null>(null);

  const current = more && more.after === first.data ? more : null;
  const prepended = added && added.after === first.data ? added.items : null;
  const removedIds = removed && removed.after === first.data ? removed.ids : null;
  const items = useMemo(() => {
    if (!first.data) return null;
    // Something prepended is the newest on the server too, so it isn't left over at the end of a
    // page: a later page repeats only what it pushed down.
    const seen = new Set<string>();
    return [...(prepended ?? []), ...first.data, ...(current?.items ?? [])].filter(({ id }) => {
      if (seen.has(id) || removedIds?.has(id)) return false;
      seen.add(id);
      return true;
    });
  }, [first.data, current, prepended, removedIds]);
  const hasMore = current ? current.hasMore : first.data?.length === PAGE_SIZE;
  const loadMoreError = failedMore && failedMore.after === first.data ? failedMore.error : null;

  // What was shown last, kept through a reload so the feed doesn't drop to placeholders and back.
  const [shown, setShown] = useState<{ items: RecentlyEditedItem[]; hasMore: boolean } | null>(
    null,
  );
  if (items && (shown?.items !== items || shown.hasMore !== hasMore)) setShown({ items, hasMore });
  const reloading = first.loading && shown !== null;

  const fetchMore = useSupabaseCallback(
    (ctx, offset: number) => fetchPage(ctx, offset, userEdited, entityType),
    [userEdited, entityType],
  );

  // The page after `listed`, which is what's shown now, or about to be.
  const loadAfter = useCallback(
    async (listed: RecentlyEditedItem[]) => {
      if (!first.data) return;
      const after = first.data;
      setLoadingMore(true);
      setFailedMore(null);
      try {
        const page = await fetchMore(listed.length);
        if (!page) return;
        // An edit since the last page moves its item to the top, shifting the rest down by one,
        // so the next page can repeat what's already shown.
        const seen = new Set(listed.map(({ id }) => id));
        setMore({
          after,
          items: [...(current?.items ?? []), ...page.filter(({ id }) => !seen.has(id))],
          hasMore: page.length === PAGE_SIZE,
        });
      } catch (error) {
        // oxlint-disable-next-line typescript/no-unsafe-type-assertion
        setFailedMore({ after, error: error as Error });
      } finally {
        setLoadingMore(false);
      }
    },
    [first.data, current, fetchMore],
  );

  const loadMore = useCallback(async () => {
    if (items) await loadAfter(items);
  }, [items, loadAfter]);

  const prepend = useCallback(
    (item: RecentlyEditedItem) => {
      if (!first.data) return;
      setAdded({ after: first.data, items: [item, ...(prepended ?? [])] });
    },
    [first.data, prepended],
  );

  const remove = useCallback(
    (id: string) => {
      if (!first.data || !items) return;
      setRemoved({ after: first.data, ids: new Set(removedIds).add(id) });
      // With everything shown archived there may still be more: load it rather than look empty.
      const rest = items.filter((item) => item.id !== id);
      if (rest.length === 0 && hasMore) void loadAfter(rest);
    },
    [first.data, items, removedIds, hasMore, loadAfter],
  );

  return {
    prepend,
    remove,
    items: reloading ? shown.items : items,
    reloading,
    error: first.error,
    reload: first.reload,
    hasMore: reloading ? shown.hasMore : hasMore,
    loadingMore,
    loadMoreError,
    loadMore,
  };
}
